import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { CheckCircle2, MoreVertical, Unplug } from 'lucide-react';
import { STATUS_BADGE_CLASSES } from '@/pages/MediaConnectors/components/AccountRow';
import { STATISTA_PLACEHOLDER_MARK } from './statistaPlaceholder';
import type { StatistaPlaceholderPaneProps } from './StatistaPlaceholderPane.types';

/**
 * Placeholder twin of `DataSourcePane`, drawn control for control so the two read as siblings
 * on the rail. Connecting changes what this pane draws and nothing else, so there is no busy
 * state to show.
 */
export function StatistaPlaceholderPane({
  connected,
  onConnectedChange,
}: StatistaPlaceholderPaneProps) {
  const { t } = useTranslation('knowledge');
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const name = t('dataSource.statista.label');

  const disconnect = () => {
    setConfirmingDisconnect(false);
    onConnectedChange(false);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-xl rounded-lg bg-card p-xl">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="grid size-6 shrink-0 place-items-center rounded border border-border-light bg-surface text-xs font-medium text-foreground"
          >
            {STATISTA_PLACEHOLDER_MARK}
          </span>
          <h2 className="text-lg font-medium text-foreground">{name}</h2>
          {connected && (
            <Badge
              variant="ghost"
              roundness="full"
              className={`${STATUS_BADGE_CLASSES.success} px-2 py-0.5 text-xs font-medium`}
            >
              <CheckCircle2 className="mr-1 size-3" />
              {t('dataSource.connected')}
            </Badge>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!connected && (
            <Button variant="outline" size="sm" onClick={() => onConnectedChange(true)}>
              {t('dataSource.activate')}
            </Button>
          )}

          {connected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('dataSource.connectionOptions')}
                  className="size-8 p-0"
                >
                  <MoreVertical className="size-4 text-foreground-alt" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  <Unplug className="mr-2 size-4" />
                  {t('dataSource.disconnect')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <p className="max-w-prose text-sm text-foreground-alt">
        {t('dataSource.statista.description')}
      </p>

      <p className="max-w-prose text-sm text-foreground-alt">
        {connected
          ? t('dataSource.statista.connectedHint')
          : t('dataSource.statista.disconnectedHint')}
      </p>

      <Dialog open={confirmingDisconnect} onOpenChange={setConfirmingDisconnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dataSource.disconnectTitle', { name })}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-alt">
              {t('dataSource.statista.disconnectDescription')}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingDisconnect(false)}>
              {t('dataSource.cancel')}
            </Button>
            <Button variant="destructive" onClick={disconnect}>
              {t('dataSource.disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

StatistaPlaceholderPane.displayName = 'StatistaPlaceholderPane';
